import DownloadCSVMenuButton, {
    downloadTypes,
} from 'components/Button/DownloadCSVMenuButton';
import { downloadItemsAsCSV, downloadTimeSeriesAsCSV } from 'utils/formatCSV';
import { filterClusters, filterNames } from 'utils/filters';
import { useCallback, useEffect } from 'react';
import {
    useCurrentCountry,
    useFilter,
    useGoogleData,
    useSort,
} from 'utils/hooks';
import { DataTable } from './DataTable';
import DrawerContent from '../DrawerContent';
import { GOOGLE_METADATA_BY_KEY } from 'utils/metrics';
import SectionTableFilterChips from '../SectionTableFilterChips';
import Stack from '../Stack';
import { TIKTOK_COUNTRIES } from '@/config/constants';
import TableHeading from '../TableHeading';
import { createDefaultColumns } from './DataTable/utils';
import sortDataByKey from 'utils/sortDataByKey';
import styled from 'styled-components';
import { useLocation } from 'react-router';
import useTableColumns from 'utils/hooks/useTableColumns';

const metrics = [
    [GOOGLE_METADATA_BY_KEY.volume, true],
    [GOOGLE_METADATA_BY_KEY.absoluteGrowth, true],
    [GOOGLE_METADATA_BY_KEY.YoY, true],
    [GOOGLE_METADATA_BY_KEY.QoQ_increase, false],
    [GOOGLE_METADATA_BY_KEY.QoQ, true],
    [GOOGLE_METADATA_BY_KEY.MoM_increase, false],
    [GOOGLE_METADATA_BY_KEY.MoM, false],
];

const TableStack = styled(Stack).attrs(
    ({ $space = (theme) => `${theme.space_9}px` }) => ({
        $space,
    }),
)`
    padding: ${({ theme }) => theme.space_9}px;
`;

const dimensionsToDrawerProps = {
    affinities: 'affinityId',
    brands: 'brandId',
    categories: 'categoryId',
    products: 'productId',
    countries: 'countryId',
};

function SectionTable({
    affinities,
    brands,
    categories,
    products,
    countries,
    paginateBy,
    controller,
    handleFilter = {
        cluster: filterClusters,
        name: filterNames,
    },
    handleSort = sortDataByKey,
    initialSortOption = {
        sortBy: 'volume',
        ascending: false,
    },
    initialFilterOption = {},
    icon,
    title,
    subtitle,
    count,
    hideCount = false,
    tools,
    tooltipKey,
    tooltipExample,
    filename = 'trend table',
    getColumnsConfig = defaultGetColumnsConfig,
    source = 'pop-index',
    ...restProps
}) {
    const country = useCurrentCountry();
    const data = Object.entries({
        affinities,
        brands,
        categories,
        products,
        countries,
    }).reduce((acc, [key, maybeIdsOrId]) => {
        acc[key] = Array.isArray(maybeIdsOrId)
            ? maybeIdsOrId
            : maybeIdsOrId
              ? [maybeIdsOrId]
              : null;
        return acc;
    }, {});
    const enabled = ![affinities, brands, categories, products, countries].some(
        (v) => v === null,
    );

    const {
        data: selectedData,
        isLoading,
        isFetching,
        isSuccess,
        isPlaceholderData,
    } = useGoogleData({
        enabled,
        data,
        paginateBy,
        includeNoDataItems: paginateBy !== 'categories',
        controller,
    });

    const {
        filteredData,
        filters,
        filtersKey,
        setFilter,
        setFilters,
        resetFilters,
    } = useFilter(selectedData, handleFilter, initialFilterOption);

    const nameFilter =
        filters && filters.name && filters.name.length ? filters.name[0] : null;

    const handleSearchInputChange = useCallback(
        (value) => {
            try {
                if (value && value !== nameFilter) {
                    setFilter('name', [value]);
                } else if (!value && nameFilter) {
                    setFilter('name', []);
                }
            } catch (err) {
                console.error(err);
            }
        },
        [nameFilter, setFilter],
    );

    let maybeFilteredData = filteredData;

    if (
        filteredData &&
        filteredData.length < 1 &&
        selectedData?.some(
            ({ fetching, fetchingData }) => fetching || fetchingData,
        )
    ) {
        maybeFilteredData = null;
    }

    const {
        setSortOption: baseSetSortOption,
        sortOption,
        sortOptionKey,
        sortedData,
        maxSortMetric,
    } = useSort(maybeFilteredData, handleSort, initialSortOption);

    const { visibleOrderedMetrics, columnsMenu, setSortOption } =
        useTableColumns({
            tableName: 'google',
            metrics,
            setSortOption: baseSetSortOption,
        });

    const visibleColumnsConfig = getColumnsConfig(visibleOrderedMetrics);

    const { pathname } = useLocation();
    useEffect(() => {
        resetFilters();
        setSortOption(initialSortOption);
    }, [pathname]);

    const fetching = isLoading || isFetching;
    const dataReady =
        !isPlaceholderData &&
        isSuccess &&
        selectedData &&
        selectedData.length > 0;

    const noDataMessage = `No data to display. ${
        filters && Object.keys(filters).length
            ? 'Try adjusting the filters'
            : ''
    }`;

    const isNotTikTokMarket =
        country && !TIKTOK_COUNTRIES.includes(country.text_id);

    return (
        <>
            <TableHeading
                icon={icon}
                title={title}
                subtitle={subtitle}
                {...(!hideCount && {
                    count:
                        count !== undefined
                            ? count
                            : selectedData && !fetching
                              ? selectedData.length
                              : null,
                })}
                tooltipKey={tooltipKey}
                tooltipExample={tooltipExample}
                tools={
                    <>
                        {tools}
                        <DownloadCSVMenuButton
                            isDisabled={!dataReady}
                            isLoading={fetching}
                            options={[
                                {
                                    mixpanelMeta: {
                                        label: 'card data csv',
                                        contentKey: 'DataTable',
                                    },
                                    children: 'Table Data CSV',
                                    onClick: () =>
                                        downloadItemsAsCSV(
                                            filename,
                                            sortedData,
                                            [
                                                ...visibleColumnsConfig,
                                                { key: 'cluster' },
                                            ],
                                        ),
                                    downloadType: downloadTypes.CSV,
                                    key: 'card data csv',
                                },
                                {
                                    mixpanelMeta: {
                                        label: 'time series csv',
                                        contentKey: 'DataTable',
                                    },
                                    children: 'Time Series CSV',
                                    onClick: () =>
                                        downloadTimeSeriesAsCSV(
                                            `${filename} time series data`,
                                            sortedData,
                                            {
                                                skipPrediction:
                                                    !isNotTikTokMarket,
                                                ...(paginateBy ===
                                                    'affinities' && {
                                                    entityColumns: [
                                                        { key: 'name' },
                                                        {
                                                            key: 'parentName',
                                                            label: 'group',
                                                        },
                                                    ],
                                                }),
                                            },
                                        ),
                                    downloadType: downloadTypes.CSV,
                                    key: 'time series csv',
                                },
                            ]}
                            mixpanelMeta={{ contentKey: 'DataTable' }}
                        />
                    </>
                }
            />
            <TableStack>
                <div
                    css={`
                        display: flex;
                        flex-wrap: nowrap;
                        gap: 0.75rem;
                        align-items: flex-start;
                    `}
                >
                    <div
                        css={`
                            flex-grow: 1;
                        `}
                    >
                        <SectionTableFilterChips
                            queryData={data}
                            handleFilter={handleFilter}
                            filters={filters}
                            setFilter={setFilter}
                            setFilters={setFilters}
                        />
                    </div>
                    {columnsMenu}
                </div>
                <DataTable
                    data={sortedData}
                    noDataMessage={noDataMessage}
                    renderDrawerContent={(selectedId) => {
                        const drawerProps = {
                            ...Object.entries(data).reduce(
                                (acc, [key, value]) => {
                                    if (!value) return acc;
                                    const drawerPropName =
                                        dimensionsToDrawerProps[key];
                                    if (key === paginateBy) {
                                        acc.paginateBy = drawerPropName;
                                        acc[drawerPropName] = selectedId;
                                        return acc;
                                    }
                                    acc[drawerPropName] = value[0];
                                    return acc;
                                },
                                { controller },
                            ),
                        };
                        return (
                            <DrawerContent
                                {...drawerProps}
                                showAddToWatchlist={
                                    drawerProps.paginateBy === 'affinityId'
                                }
                                skipPrediction={!isNotTikTokMarket}
                                source={source}
                            />
                        );
                    }}
                    sortOption={sortOption}
                    setSortOption={setSortOption}
                    maxSortMetric={maxSortMetric}
                    onSearchInputChange={handleSearchInputChange}
                    dataKey={`d-*-s-${sortOptionKey}-f-${filtersKey}`}
                    columns={visibleColumnsConfig}
                    {...restProps}
                />
            </TableStack>
        </>
    );
}

const defaultGetColumnsConfig = (visibleOrderedMetrics) => {
    const columnsConfig = createDefaultColumns([
        'name',
        ...visibleOrderedMetrics,
    ]);
    return columnsConfig;
};

export default SectionTable;
